import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Column } from 'primereact/column'
import type { LocalizedText } from '../../../lib/localizedText'
import Button from '../../../components/ui/Button'
import SortSpinner from '../../../components/ui/SortSpinner'
import type { MeetingDecisionType } from '../types'

interface ColumnOpts {
  t: TFunction
  /** Ad kolonu tek kolondur; değer seçili dilden gelir (`lib/localizedText`). */
  localized: LocalizedText
  /** Görünen satır sayısı — son satırda "aşağı" pasif olması için. */
  rowCount: number
  /** Yeniden sıralama sürüyor mu — oklar kilitlenir. */
  reordering: boolean
  onMove: (row: MeetingDecisionType, dir: 'up' | 'down') => void
  onEdit: (row: MeetingDecisionType) => void
  /** 126 — Kısıtlı Yetkili asistan: sıra ve düzenle kolonları hiç çizilmez (salt okunur). */
  readOnly?: boolean
}

/**
 * Toplantı Kayıt Tipi tablosunun sütunları: baştaki **sıra** kolonu (↑/↓ ok, SORT_INDEX — Davet Türü ile
 * aynı kalıp; sıra numarası gösterilmez, taşıma anında kalıcı) + ad + "Düzenle". Kayıt kümesi sabit
 * (ekle/durum/sil yok); yalnız sıralama + ad düzenleme.
 *
 * <p>Ad <b>tek kolondur</b> ve değeri seçili dilden gelir (24.09.2026 kullanıcı kararı); form iki adı da
 * düzenler. Dar ekran kart listesi (sayfanın kendi dalı) da aynı değeri gösterir — orası bir kez geride
 * kaldı ve kullanıcı "dil değişince ad değişmiyor" diye bildirdi.
 */
export function meetingDecisionTypeColumns({
  t,
  localized,
  rowCount,
  reordering,
  onMove,
  onEdit,
  readOnly = false,
}: ColumnOpts): ReactElement[] {
  const nameColumn = (
    <Column
      key="name"
      header={t('activity.meetingDecisionTypes.columns.name')}
      body={(row: MeetingDecisionType) => localized(row.nameTr, row.nameEn)}
    />
  )
  // Salt okunur kipte sıra (↑/↓) ve "Düzenle" kolonları yalnız yazma işi taşır → yalnız ad kalır.
  if (readOnly) return [nameColumn]
  return [
    <Column
      key="sort"
      header={t('activity.meetingDecisionTypes.columns.sortIndex')}
      style={{ width: '6rem' }}
      body={(row: MeetingDecisionType, { rowIndex }) => (
        <SortSpinner
          onUp={() => onMove(row, 'up')}
          onDown={() => onMove(row, 'down')}
          disableUp={rowIndex === 0}
          disableDown={rowIndex === rowCount - 1}
          busy={reordering}
          labelUp={t('common.reorder.up')}
          labelDown={t('common.reorder.down')}
        />
      )}
    />,
    nameColumn,
    <Column
      key="actions"
      header={t('activity.meetingDecisionTypes.columns.actions')}
      style={{ width: '8rem' }}
      body={(row: MeetingDecisionType) => (
        <div className="admin-row-actions">
          <Button
            icon="pi pi-pencil"
            rounded
            text
            aria-label={t('activity.meetingDecisionTypes.actions.edit')}
            tooltip={t('activity.meetingDecisionTypes.actions.edit')}
            tooltipOptions={{ position: 'bottom' }}
            onClick={() => onEdit(row)}
          />
        </div>
      )}
    />,
  ]
}
